import React, { ChangeEvent } from "react";
import { ImagePlus, Trash2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface ExistingImage {
  imageUrl: string;
}

interface Props {
  existingImages: ExistingImage[];
  setExistingImages: (images: ExistingImage[]) => void;
  newImages: File[];
  setNewImages: (images: File[]) => void;
  maxImages?: number;
  label: string;
}

const ImageUploaderUpdate: React.FC<Props> = ({
  existingImages,
  setExistingImages,
  newImages,
  setNewImages,
  maxImages = 5,
  label,
}) => {
  const { t } = useTranslation();

  const totalImages = existingImages.length + newImages.length;

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
      const maxSizeInMB = 1; // max 1 MB
      if (e.target.files[0].size > maxSizeInMB * 1024 * 1024) {
        alert(`File is too large. Max size is ${maxSizeInMB} MB`);
        return;
      }

    const files = Array.from(e.target.files);

    if (totalImages + files.length > maxImages) {
      alert(`You can only upload up to ${maxImages} images.`);
      return;
    }

    setNewImages([...newImages, ...files]);
    e.target.value = "";
  };

  const handleRemoveExisting = (index: number) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const handleRemoveNew = (index: number) => {
    setNewImages(newImages.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-wrap gap-4">
      {/* Add Photo input */}
      {totalImages < maxImages && (
        <label className="w-28 h-28 flex flex-col items-center justify-center border-2 border-dashed rounded-2xl cursor-pointer hover:border-primary transition relative">
          <ImagePlus className="text-2xl text-gray-400" />
          <span className="text-xs mt-1 text-gray-500">{t(label)}</span>
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
        </label>
      )}

      {/* Existing images previews */}
      {existingImages.map((img, index) => (
        <div
          key={`existing-${index}`}
          className="relative w-28 h-28 rounded-2xl overflow-hidden shadow-md"
        >
          <img
            src={img.imageUrl}
            alt={`existing-${index}`}
            className="w-full h-full object-cover"
          />
          <button
            onClick={() => handleRemoveExisting(index)}
            className="absolute top-1 right-1 text-red-600 bg-white rounded-full w-6 h-6 flex items-center justify-center"
          >
            <Trash2Icon size={14} />
          </button>
        </div>
      ))}

      {/* New images previews */}
      {newImages.map((file, index) => (
        <div
          key={`new-${index}`}
          className="relative w-28 h-28 rounded-2xl overflow-hidden shadow-md"
        >
          <img
            src={URL.createObjectURL(file)}
            alt={`new-${index}`}
            className="w-full h-full object-cover"
          />
          <button
            onClick={() => handleRemoveNew(index)}
            className="absolute top-1 right-1 text-red-600 bg-white rounded-full w-6 h-6 flex items-center justify-center"
          >
            <Trash2Icon size={14} />
          </button>
        </div>
      ))}
    </div>
  );
};

export default ImageUploaderUpdate;
